let _categoriesCache = null;

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this._inflightUrl = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this._debouncedOnChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.input.addEventListener('input', this._debouncedOnChange);
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.querySelector('form')?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.loadCategories();

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  loadCategories() {
    const container = this.querySelector('.search__types[data-categories-url]');
    if (!container) return;

    const populate = ({ options, filterParam }) => {
      if (!options.length) return;
      const select = this.searchProductTypes;
      if (!select) return;
      options.forEach(({ value, label }) => select.appendChild(new Option(label, value)));
      if (filterParam) container.dataset.filterParam = filterParam;
      container.classList.remove('hidden');
    };

    if (_categoriesCache) {
      populate(_categoriesCache);
      return;
    }

    fetch(container.dataset.categoriesUrl)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const selectEl = doc.querySelector('select');
        const filterParam = selectEl?.dataset.filterParam || '';
        const options = [...(selectEl?.querySelectorAll('option') || [])]
          .map((o) => ({ value: o.value, label: o.textContent.trim() }))
          .filter((o) => o.value)
          .sort((a, b) => a.label.localeCompare(b.label));
        _categoriesCache = { options, filterParam };
        populate(_categoriesCache);
      })
      .catch((err) => console.error('Failed to load categories:', err));
  }

  updateViewAllLink(resultsContainer) {
    const categoryValue = this.searchProductTypes?.value;
    const typesContainer = this.querySelector('.search__types');
    const filterParam = typesContainer?.dataset.filterParam;
    if (!categoryValue || !filterParam) return;

    const link = resultsContainer.querySelector('.search__results-all a');
    if (!link) return;

    const searchBase = typesContainer.dataset.categoriesUrl?.split('?')[0] || '/search';
    const url = new URL(`${window.shopUrl}${searchBase}`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('type', 'product');
    url.searchParams.set(filterParam, categoryValue);
    link.href = url.toString();
  }

  onFormSubmit(event) {
    const categoryValue = this.searchProductTypes?.value;
    const container = this.querySelector('.search__types');
    const filterParam = container?.dataset.filterParam;
    if (!categoryValue || !filterParam) return;

    event.preventDefault();
    const searchBase = container.dataset.categoriesUrl?.split('?')[0] || '/search';
    const url = new URL(`${window.shopUrl}${searchBase}`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('type', 'product');
    url.searchParams.set(filterParam, categoryValue);
    window.location.href = url.toString();
  }

  handleProductTypeChange(evt) {
    this._debouncedOnChange.cancel?.();
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const search_term = this.getQuery();
    const categoryValue = this.searchProductTypes?.value;
    const container = this.querySelector('.search__types');
    const filterParam = container?.dataset.filterParam;

    if (categoryValue && filterParam) {
      const searchBase = container.dataset.categoriesUrl?.split('?')[0] || '/search';
      const url = new URL(`${window.shopUrl}${searchBase}`);
      url.searchParams.set('q', search_term);
      url.searchParams.set('type', 'product');
      url.searchParams.set(filterParam, categoryValue);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
      return url;
    }

    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    if (this.abortController && this._inflightUrl === url) return;
    this.abortPendingFetch();
    this._inflightUrl = url;
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  abortPendingFetch() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this._inflightUrl = null;
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);
    this.abortController = new AbortController();

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        if (this._inflightUrl === url) {
          this.abortController = null;
          this._inflightUrl = null;
          this.setLoadingState(false);
        }
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.updateViewAllLink(targetElement);

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
      ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
        target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
